import {NgTemplateOutlet} from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {firstValueFrom, filter, map, startWith} from 'rxjs';
import type {AuthenticatedUser} from '../../models/auth.models';
import {AuthService} from '../../services/auth.service';
import {PageInfoService} from '../../services/page-info.service';

/** Routes that render without the top toolbar (auth pages). */
const AUTH_ROUTES = ['/login', '/auth'] as const;

interface PrimaryTab {
  label: string;
  route: string;
  icon: string;
}

const PRIMARY_TABS: readonly PrimaryTab[] = [
  {label: 'Valuations', route: '/valuations', icon: 'calculator'},
  {label: 'Sales', route: '/sales', icon: 'money'}
] as const;

@Component({
  selector: 'app-app-shell',
  imports: [NgTemplateOutlet, RouterLink],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppShellComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  protected readonly pageInfo = inject(PageInfoService);
  protected readonly tabs = PRIMARY_TABS;
  protected readonly currentUser = signal<AuthenticatedUser | null>(null);
  protected readonly userMenuOpen = signal(false);
  protected readonly userInitials = computed(() => {
    const displayName = this.currentUser()?.display_name ?? '';
    return displayName
      .split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    {initialValue: this.router.url},
  );

  constructor() {
    void this.loadAuthState();
  }

  /** True when the active route is an auth page — toolbar not rendered. */
  protected readonly isAuthPage = computed(() => {
    const url = this.currentUrl() ?? '';
    return AUTH_ROUTES.includes(url as (typeof AUTH_ROUTES)[number]);
  });

  protected isActiveTab(route: string): boolean {
    const url = this.currentUrl() ?? '';
    return url === route || url.startsWith(`${route}/`);
  }

  protected triggerSearch(): void {
    const isMac = /Mac|iPhone|iPad/i.test(navigator.platform);
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'k',
        metaKey: true,
        ctrlKey: !isMac,
      }),
    );
  }

  protected toggleUserMenu(): void {
    if (this.currentUser()) {
      this.userMenuOpen.update((open) => !open);
    }
  }

  protected signOut(): void {
    this.userMenuOpen.set(false);
    this.currentUser.set(null);
    this.authService.logout();
  }

  @HostListener('document:mousedown', ['$event'])
  protected onDocumentMouseDown(event: MouseEvent): void {
    if (!this.userMenuOpen()) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && !this.hostElement.nativeElement.contains(target)) {
      this.userMenuOpen.set(false);
    }
  }

  private async loadAuthState(): Promise<void> {
    try {
      const response = await firstValueFrom(this.authService.getCurrentUser());
      this.currentUser.set(response.user);
    } catch {
      this.currentUser.set(null);
    }
  }
}
